import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { matchedJobs } from '../data/foryou';
import { allJobs } from '../data/jobs';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataProvider';
import { fetchJobById } from '../adapters/jobAdapter';
import { fetchBatchesForAnnotator } from '../services/batchService';
import { logHours as logTimeEntry, fetchEntriesForAssignment } from '../services/timeEntryService';
import { safeQuery } from '../utils/safeSupabase';
import { resolveAssignmentRate } from '../utils/projectMetrics';
import { supabase } from '../lib/supabase';
import JobBatchQueue from '../components/JobBatchQueue';
import { activateOnEnterOrSpace } from '../utils/a11y';

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { appliedJobs, acceptedJobs, startedJobs, savedJobs, toggleSaveJob, applyToJob } = useApp();
  const { user: authUser } = useAuth();
  const { currentUser, dataSource } = useData();
  const numericId = id && /^\d+$/.test(id) ? Number(id) : null;
  const matchedJob = numericId !== null ? matchedJobs.find(p => p.id === numericId) : null;
  const fullJob = numericId !== null ? allJobs.find(p => p.id === numericId) : null;
  const legacyJob = useMemo(() => matchedJob ? { ...matchedJob, details: matchedJob.details || fullJob?.details } : fullJob, [matchedJob, fullJob]);
  const [liveJob, setLiveJob] = useState(null);
  const [jobLoading, setJobLoading] = useState(true);
  const [assignment, setAssignment] = useState(null);
  const [jobBatches, setJobBatches] = useState([]);
  const [applyState, setApplyState] = useState('idle');
  const [descExpanded, setDescExpanded] = useState(true);
  const [timeEntries, setTimeEntries] = useState([]);
  const [hourInput, setHourInput] = useState('');
  const [itemsInput, setItemsInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [logStatus, setLogStatus] = useState(null); // 'saving'|'saved'|'error'
  const [qualityFromLogs, setQualityFromLogs] = useState(null); // avg accuracy from quality_log

  const job = liveJob || legacyJob;
  const jobId = numericId ?? id;
  const saved = savedJobs.has(jobId);
  const isAssigned = !!assignment;
  const isActive = assignment?.status === 'Active';
  const isOnboarding = assignment?.status === 'Onboarding';
  const isApplied = appliedJobs.has(jobId);
  const isStarted = startedJobs.has(jobId);

  // Fetch job + assignment + batches
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setJobLoading(true);
      const uid = authUser?.id;

      // Always try live fetch first; fall back to legacy static data
      if (id) {
        const data = await fetchJobById(id);
        if (!cancelled && data) setLiveJob(data);
      }

      // Fetch assignment for this user + job
      if (uid && id && dataSource === 'supabase') {
        const { data: assigns } = await safeQuery(
          supabase.from('assignment')
            .select('id, status, hours_logged, rate, confirmed_rate, quality_score, annotations_completed, assigned_at, started_at, completed_at, pay_confirmed, total_earned')
            .eq('individual_id', uid)
            .eq('job_id', id)
            .in('status', ['Active', 'Onboarding', 'Completed'])
            .order('assigned_at', { ascending: false })
            .limit(1),
          'jobDetail-fetchAssignment'
        );
        if (!cancelled && assigns?.length > 0) setAssignment(assigns[0]);

        // Fetch quality_log to compute accurate avg quality for this annotator on this job
        const { data: qRows } = await safeQuery(
          supabase.from('quality_log')
            .select('accuracy_percent')
            .eq('individual_id', uid)
            .eq('job_id', id),
          'jobDetail-fetchQualityLog'
        );
        if (!cancelled && qRows && qRows.length > 0) {
          const valid = qRows.filter(q => q.accuracy_percent != null);
          if (valid.length > 0) {
            const avg = valid.reduce((s, q) => s + Number(q.accuracy_percent), 0) / valid.length;
            setQualityFromLogs(avg);
          }
        }

        // Fetch batches for this user on this job
        const allBatches = await fetchBatchesForAnnotator(uid);
        if (!cancelled) setJobBatches((allBatches || []).filter(b => b.job_id === id));

        // Fetch time entries for this assignment
        if (assigns?.length > 0) {
          const entries = await fetchEntriesForAssignment(assigns[0].id, { limit: 30 });
          if (!cancelled) {
            setTimeEntries(entries);
            // Pre-fill today's hours if an entry exists
            const d = new Date();
            const today = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
            const todayEntry = entries.find(e => e.date === today);
            if (todayEntry) setHourInput(String(todayEntry.hours));
          }
        }
      }

      if (!cancelled) setJobLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [id, dataSource, authUser?.id]);

  // Sync apply state
  useEffect(() => {
    if (appliedJobs.has(jobId)) setApplyState('applied');
  }, [appliedJobs, jobId]);

  const loadBatches = async () => {
    if (!authUser?.id) return;
    const data = await fetchBatchesForAnnotator(authUser.id);
    setJobBatches((data || []).filter(b => b.job_id === id));
  };

  // Local date string helper (avoids UTC issues, computed once)
  const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();

  if (jobLoading && !job) {
    return (
      <div style={{ padding: 48 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: "var(--text-muted)", padding: 0 }}>← Back</button>
        <p style={{ marginTop: 24, color: "var(--text-muted)" }}>Loading job…</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div style={{ padding: 48 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: "var(--text-muted)", padding: 0 }}>← Back</button>
        <p style={{ marginTop: 24, color: "var(--text-muted)" }}>Job not found.</p>
      </div>
    );
  }

  // Compute personal metrics from assignment + batches
  const hoursLogged = assignment?.hours_logged || 0;
  // R10 MED-3 + R13 LOW-1 audit fix: when an assignment exists, honor the
  // canonical resolver WITHOUT a `job.rate` fallback — a volunteer (rate=0)
  // correctly sees $0 earned, matching projectJob's dollarsSpent semantic.
  // The `job.rate` fallback survives only in the "browsing, no assignment
  // yet" path, where it serves as the posting-level default shown in the
  // job header before the user applies.
  const rate = assignment ? (resolveAssignmentRate(assignment) || 0) : (job?.rate || 0);
  // Prefer computed avg from quality_log (authoritative) over stale assignment.quality_score
  const quality = qualityFromLogs != null ? Math.round(qualityFromLogs) : (assignment?.quality_score || 0);
  const personalAnnotations = jobBatches.reduce((s, b) => s + (b.items_completed || 0), 0);
  const personalTotal = jobBatches.reduce((s, b) => s + (b.total_items || 0), 0);
  // Use whichever is higher: total_earned (includes bonuses) or computed (always current after trigger)
  const computedEarned = hoursLogged * rate;
  const earned = Math.max(assignment?.total_earned || 0, computedEarned);

  // ─── MODE 2: Working (assigned to this job) ───
  if (isAssigned || isStarted) {
    return (
      <div>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <button onClick={() => navigate('/my-work')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: "var(--text-muted)", padding: 0, marginBottom: 8 }}>← Back to My Work</button>
            <h1 style={{ fontSize: 24, fontWeight: 600, color: "var(--text-primary)", margin: '0 0 4px 0' }}>{job.title}</h1>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {job.domain}{job.duration ? ` · ${job.duration}` : ''}
              <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 500, color: isActive ? 'var(--text-success)' : isOnboarding ? 'var(--text-warning)' : 'var(--text-muted)' }}>
                {assignment?.status || 'Active'}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {(isActive || isOnboarding) && (
              <button onClick={() => window.open('https://platform.pipelines.tech', '_blank')} style={{
                fontSize: 11, fontWeight: 500, padding: '6px 16px', borderRadius: 6, cursor: 'pointer', border: 'none',
                background: "var(--accent-teal)", color: "var(--text-primary)", whiteSpace: 'nowrap',
              }}>Start Working →</button>
            )}
            <button onClick={() => toggleSaveJob(jobId)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <svg width="16" height="18" viewBox="0 0 14 16" fill={saved ? 'var(--text-success)' : 'none'} stroke={saved ? 'var(--text-success)' : 'var(--text-muted)'} strokeWidth="1.5"><path d="M1.5 1.5H12.5V14.5L7 11L1.5 14.5V1.5Z"/></svg>
            </button>
          </div>
        </div>

        {/* KPI stat row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0, marginBottom: 24 }}>
          {[
            { value: `${Math.round(hoursLogged * 10) / 10}h`, label: 'Hours Logged' },
            { value: personalTotal > 0 ? `${personalAnnotations}/${personalTotal}` : `${personalAnnotations}`, label: 'Annotations' },
            { value: quality > 0 ? `${quality}%` : '—', label: 'Quality' },
            { value: `$${earned > 1000 ? (earned / 1000).toFixed(1) + 'K' : earned.toFixed(0)}`, label: 'Earned' },
          ].map((kpi, i, arr) => (
            <div key={kpi.label} style={{ textAlign: 'center', padding: '12px 8px', borderRight: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ fontSize: 28, fontWeight: 600, fontFamily: 'var(--font-mono)', color: "var(--text-primary)", lineHeight: 1 }}>{kpi.value}</div>
              <div style={{ fontSize: 9, fontWeight: 500, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px', marginTop: 6 }}>{kpi.label}</div>
            </div>
          ))}
        </div>

        {/* Two-column: Time Log (left) + Job Info & Description (right) */}
        {isActive && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px' }}>Time Log</span>
              <span style={{ fontSize: 10, color: "var(--text-muted)" }}>
                {timeEntries.reduce((s, e) => s + (parseFloat(e.hours) || 0), 0).toFixed(1)}h total logged
              </span>
            </div>
            {/* Log today's hours */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '10px 12px', background: "var(--bg-stat)", borderRadius: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text-primary)", flexShrink: 0 }}>Today</span>
              <input type="number" step="0.5" min="0" max="24" placeholder="0"
                value={hourInput}
                onChange={e => setHourInput(e.target.value)}
                style={{ width: 56, padding: '6px 8px', fontSize: 13, fontFamily: 'var(--font-mono)', border: '1px solid var(--border)', borderRadius: 4, textAlign: 'center', outline: 'none', color: "var(--text-primary)" }}
                onFocus={e => { e.target.style.borderColor = 'var(--text-success)'; }}
                onBlur={e => { e.target.style.borderColor = 'var(--border)'; }}
              />
              <span style={{ fontSize: 10, color: "var(--text-muted)" }}>h</span>
              <input type="number" step="1" min="0" placeholder="0"
                value={itemsInput}
                onChange={e => setItemsInput(e.target.value)}
                style={{ width: 68, padding: '6px 8px', fontSize: 13, fontFamily: 'var(--font-mono)', border: '1px solid var(--border)', borderRadius: 4, textAlign: 'center', outline: 'none', color: "var(--text-primary)" }}
                onFocus={e => { e.target.style.borderColor = 'var(--text-success)'; }}
                onBlur={e => { e.target.style.borderColor = 'var(--border)'; }}
              />
              <span style={{ fontSize: 10, color: "var(--text-muted)" }}>items</span>
              <input type="text" placeholder="What did you work on?" value={noteInput}
                onChange={e => setNoteInput(e.target.value)}
                style={{ flex: 1, padding: '6px 10px', fontSize: 11, border: '1px solid var(--border)', borderRadius: 4, outline: 'none', color: "var(--text-primary)" }}
                onFocus={e => { e.target.style.borderColor = 'var(--text-success)'; }}
                onBlur={e => { e.target.style.borderColor = 'var(--border)'; }}
              />
              <button disabled={logStatus === 'saving' || !(parseFloat(hourInput) > 0)}
                onClick={async () => {
                  const hours = parseFloat(hourInput);
                  if (!hours || hours <= 0) return;
                  setLogStatus('saving');
                  const items = parseInt(itemsInput, 10);
                  const { error } = await logTimeEntry({
                    assignmentId: assignment.id,
                    jobId: id,
                    orgId: currentUser?.org_id || null,
                    hours,
                    items: Number.isFinite(items) && items >= 0 ? items : undefined,
                    note: noteInput || undefined,
                  });
                  if (error) {
                    setLogStatus('error');
                    setTimeout(() => setLogStatus(null), 2000);
                  } else {
                    setLogStatus('saved');
                    setItemsInput('');
                    setNoteInput('');
                    // Refresh entries
                    const entries = await fetchEntriesForAssignment(assignment.id, { limit: 30 });
                    setTimeEntries(entries);
                    setTimeout(() => setLogStatus(null), 2000);
                  }
                }}
                style={{
                  padding: '6px 16px', fontSize: 11, fontWeight: 500, borderRadius: 4, cursor: 'pointer', border: 'none', flexShrink: 0,
                  background: logStatus === 'saved' ? 'var(--text-success)' : logStatus === 'error' ? 'var(--text-error)' : timeEntries.some(e => e.date === todayStr) ? 'var(--border)' : 'var(--text-success)',
                  color: logStatus === 'saved' || !timeEntries.some(e => e.date === todayStr) ? 'var(--bg-card)' : logStatus === 'error' ? 'var(--bg-card)' : 'var(--text-primary)',
                  opacity: logStatus === 'saving' || !(parseFloat(hourInput) > 0) ? 0.5 : 1,
                }}
              >{logStatus === 'saving' ? '...' : logStatus === 'saved' ? '✓ Saved' : logStatus === 'error' ? 'Error' : timeEntries.some(e => e.date === todayStr) ? 'Update' : 'Log Hours'}</button>
            </div>
            {/* Recent time entries table */}
            {timeEntries.length > 0 && (
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 10, fontWeight: 600, color: "var(--text-muted)", textTransform: 'uppercase' }}>Date</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: 10, fontWeight: 600, color: "var(--text-muted)", textTransform: 'uppercase' }}>Hours</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: 10, fontWeight: 600, color: "var(--text-muted)", textTransform: 'uppercase' }}>Items</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 10, fontWeight: 600, color: "var(--text-muted)", textTransform: 'uppercase' }}>Note</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: 10, fontWeight: 600, color: "var(--text-muted)", textTransform: 'uppercase' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeEntries.slice(0, 14).map(entry => (
                      <tr key={entry.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                        <td style={{ padding: '6px 8px', color: "var(--text-primary)" }}>
                          {new Date(entry.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 500, color: "var(--text-primary)" }}>
                          {parseFloat(entry.hours).toFixed(1)}h
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: entry.items_completed > 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                          {entry.items_completed > 0 ? Number(entry.items_completed).toLocaleString() : '—'}
                        </td>
                        <td style={{ padding: '6px 8px', color: "var(--text-muted)", maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {entry.note || '—'}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                          <span style={{
                            fontSize: 9, fontWeight: 500, padding: '1px 6px', borderRadius: 3,
                            background: entry.status === 'approved' ? 'var(--bg-success)' : entry.status === 'rejected' ? 'var(--bg-error)' : 'var(--bg-warning)',
                            color: entry.status === 'approved' ? 'var(--text-success)' : entry.status === 'rejected' ? 'var(--text-error)' : 'var(--text-warning)',
                          }}>{entry.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {timeEntries.length === 0 && (
              <div style={{ fontSize: 11, color: "var(--text-muted)", textAlign: 'center', padding: '8px 0' }}>No hours logged yet for this project</div>
            )}

            {/* Batch Queue + Client Feedback (below Time Log, same left column) */}
            <div style={{ marginTop: 16 }}>
              <JobBatchQueue batches={jobBatches} onRefresh={loadBatches} />
              {jobBatches.length === 0 && (
                <div style={{ padding: '24px', textAlign: 'center', fontSize: 12, color: "var(--text-muted)", background: "var(--bg-card)", borderRadius: 8 }}>No batches assigned yet</div>
              )}
              {(() => {
                const reviewedBatches = jobBatches.filter(b => (b.items_accepted || 0) > 0 || (b.items_rejected || 0) > 0);
                if (reviewedBatches.length === 0) return null;
                return (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>Client Feedback ({reviewedBatches.length})</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {reviewedBatches.map(b => {
                        const total = (b.items_accepted || 0) + (b.items_rejected || 0);
                        const accRate = total > 0 ? Math.round((b.items_accepted / total) * 100) : 0;
                        return (
                          <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: "var(--bg-stat)", borderRadius: 6, border: '1px solid var(--border)' }}>
                            <span style={{ fontSize: 13, fontFamily: 'var(--font-mono)', fontWeight: 500, color: "var(--text-primary)", minWidth: 50 }}>#{b.batch_number}</span>
                            <span style={{ fontSize: 12, color: accRate >= 90 ? 'var(--text-success)' : accRate >= 75 ? 'var(--text-warning)' : 'var(--text-error)', fontWeight: 600, minWidth: 40 }}>{accRate}%</span>
                            <span style={{ fontSize: 12, color: "var(--text-muted)", flex: 1 }}>{b.items_accepted || 0} accepted · {b.items_rejected || 0} rejected</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Start Working — aligned right, below client feedback */}
              {(isActive || isOnboarding) && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                  <button onClick={() => window.open('https://platform.pipelines.tech', '_blank')} style={{
                    fontSize: 11, fontWeight: 500, padding: '6px 16px', borderRadius: 6, cursor: 'pointer', border: 'none',
                    background: "var(--accent-teal)", color: "var(--text-primary)",
                  }}>Start Working →</button>
                </div>
              )}
            </div>
          </div>

          {/* Right column: Job Info + Description in one box */}
          <div style={{ alignSelf: 'start' }}>
            <div style={{ background: "var(--bg-card)", borderRadius: 8, padding: 16, boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.48px', marginBottom: 12 }}>Job Info</div>
              {(() => {
                const firstBatchStart = jobBatches.filter(b => b.started_at).map(b => new Date(b.started_at).getTime()).sort((a, b) => a - b)[0];
                const daysActive = firstBatchStart ? Math.max(1, (Date.now() - firstBatchStart) / 86400000) : 0;
                const currentPace = daysActive > 0 && personalAnnotations > 0 ? Math.round(personalAnnotations / daysActive) : null;
                const remaining = personalTotal - personalAnnotations;
                const dl = job.endDate ? new Date(job.endDate) : null;
                const daysLeftVal = dl ? Math.max(0, Math.ceil((dl - Date.now()) / 86400000)) : null;
                const neededPace = daysLeftVal && daysLeftVal > 0 && remaining > 0 ? Math.round(remaining / daysLeftVal) : null;
                return [
                  { label: 'Rate', value: `$${rate}/hr` },
                  { label: 'Project Start', value: job.startDate || '—' },
                  ...(assignment?.assigned_at ? [{ label: 'My Start Date', value: new Date(assignment.assigned_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }] : []),
                  { label: 'Deadline', value: job.endDate || '—' },
                  ...(currentPace != null ? [{ label: 'My Pace', value: `${currentPace} items/day` }] : []),
                  ...(neededPace != null ? [{ label: 'Needed Pace', value: `${neededPace} items/day`, color: currentPace >= neededPace ? 'var(--text-success)' : 'var(--text-error)' }] : []),
                  ...(daysLeftVal != null ? [{ label: 'Days Left', value: `${daysLeftVal}`, color: daysLeftVal < 7 ? 'var(--text-error)' : undefined }] : []),
                  { label: 'Team Size', value: job.teamSize ? `${job.teamSize} annotators` : '—' },
                  { label: 'Spots Filled', value: `${job.currentStaffed || '?'}/${job.teamSize || '?'}` },
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{item.label}</span>
                    <span style={{ fontSize: 11, fontWeight: 500, color: item.color || 'var(--text-primary)' }}>{item.value}</span>
                  </div>
                ));
              })()}

              {/* Description inside same box with show more */}
              {(job.overview || job.description) && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                  <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={descExpanded}
                    onClick={() => setDescExpanded(!descExpanded)}
                    onKeyDown={activateOnEnterOrSpace(() => setDescExpanded(!descExpanded))}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: descExpanded ? 8 : 0 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px' }}>Description</span>
                    <span style={{ fontSize: 10, color: "var(--text-muted)" }}>{descExpanded ? 'Show less' : 'Show more'}</span>
                  </div>
                  {!descExpanded && (
                    <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5, maxHeight: 40, overflow: 'hidden' }}>
                      {job.overview || job.description || ''}
                    </div>
                  )}
                  {descExpanded && (
                    <div style={{ fontSize: 11, color: "var(--text-primary)", lineHeight: 1.6, maxHeight: 300, overflowY: 'auto' }}>
                      {job.overview && <div style={{ marginBottom: 10 }}>{job.overview}</div>}
                      {job.responsibilities?.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 9, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', marginBottom: 4 }}>Responsibilities</div>
                          {job.responsibilities.map((r, i) => <div key={i} style={{ padding: '2px 0', paddingLeft: 12 }}>{i + 1}. {r}</div>)}
                        </div>
                      )}
                      {job.requirements?.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 9, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', marginBottom: 4 }}>Requirements</div>
                          {job.requirements.map((r, i) => <div key={i} style={{ padding: '2px 0', paddingLeft: 12 }}>• {r}</div>)}
                        </div>
                      )}
                      {job.details?.sampleTask && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 9, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', marginBottom: 4 }}>Sample Task</div>
                          <div style={{ padding: '6px 10px', background: "var(--bg-stat)", borderRadius: 4, border: '1px solid var(--border)', fontFamily: 'var(--font-mono)', fontSize: 10 }}>{job.details.sampleTask}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          </div>
        )}

        {/* Start Working CTA moved into left column after Client Feedback */}
      </div>
    );
  }

  // ─── MODE 1: Browsing (not assigned) — structured job posting ───
  // Show actual project start date as-is

  const rateDisplay = typeof job.rate === 'number' ? `$${job.rate}/hr` : job.rate;
  const rateRange = job.rateRange?.min && job.rateRange?.max ? `$${job.rateRange.min}–$${job.rateRange.max}/hr` : rateDisplay;
  const briefDesc = job.briefDescription || job.brief_description || '';
  // Split overview into paragraphs for readability
  const overviewParagraphs = (job.overview || '').split(/\n\n|\n/).filter(p => p.trim().length > 0);

  return (
    <div>
      <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: "var(--text-muted)", padding: 0, marginBottom: 16 }}>← Back</button>

      {/* ─── Header card ─── */}
      <div style={{ background: "var(--bg-card)", borderRadius: 8, padding: '24px', boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 600, color: "var(--text-primary)", margin: '0 0 6px 0' }}>{job.title}</h1>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: "var(--text-muted)" }}>
              <span>{job.domain}</span>
              {job.duration && <><span>·</span><span>{job.duration}</span></>}
              {job.location && <><span>·</span><span>{job.location}</span></>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 22, fontFamily: 'var(--font-mono)', fontWeight: 600, color: "var(--text-primary)" }}>{rateRange}</div>
              {job.referralBonus && <div style={{ fontSize: 10, color: "var(--text-muted)" }}>+${job.referralBonus} referral</div>}
            </div>
            <button onClick={() => toggleSaveJob(jobId)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <svg width="16" height="18" viewBox="0 0 14 16" fill={saved ? 'var(--text-success)' : 'none'} stroke={saved ? 'var(--text-success)' : 'var(--text-muted)'} strokeWidth="1.5"><path d="M1.5 1.5H12.5V14.5L7 11L1.5 14.5V1.5Z"/></svg>
            </button>
          </div>
        </div>

        {/* Brief description */}
        {briefDesc && <div style={{ fontSize: 13, color: "var(--text-primary)", lineHeight: 1.6, marginBottom: 16 }}>{briefDesc}</div>}

        {/* Key details grid (mirrors form structure) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          {[
            { label: 'Spots Available', value: `${job.currentStaffed || 0} / ${job.teamSize || '?'}` },
            { label: 'Hours / Week', value: job.hoursPerWeek || '—' },
            { label: 'Start Date', value: job.startDate || '—' },
            { label: 'Posted', value: job.posted || '—' },
          ].map(item => (
            <div key={item.label} style={{ padding: '10px', background: "var(--bg-stat)", borderRadius: 6 }}>
              <div style={{ fontSize: 9, fontWeight: 500, color: "var(--text-muted)", textTransform: 'uppercase', marginBottom: 3 }}>{item.label}</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Two-column layout: Description + Sidebar ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16, marginBottom: 16 }}>

        {/* Left: Job description sections */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Overview */}
          {overviewParagraphs.length > 0 && (
            <div style={{ background: "var(--bg-card)", borderRadius: 8, padding: '18px 20px', boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 10 }}>About This Role</div>
              <div style={{ maxHeight: 300, overflowY: 'auto', paddingRight: 4 }}>
                {overviewParagraphs.map((p, i) => (
                  <div key={i} style={{ fontSize: 13, color: "var(--text-primary)", lineHeight: 1.7, marginBottom: 12 }}>{p}</div>
                ))}
              </div>
            </div>
          )}

          {/* Responsibilities */}
          {job.responsibilities?.length > 0 && (
            <div style={{ background: "var(--bg-card)", borderRadius: 8, padding: '18px 20px', boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 10 }}>What You'll Do</div>
              {job.responsibilities.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: "var(--text-primary)", padding: '5px 0', lineHeight: 1.5 }}>
                  <span style={{ color: "var(--text-muted)", flexShrink: 0, fontFamily: 'var(--font-mono)', fontSize: 11 }}>{i + 1}.</span>
                  <span>{r}</span>
                </div>
              ))}
            </div>
          )}

          {/* Requirements */}
          {job.requirements?.length > 0 && (
            <div style={{ background: "var(--bg-card)", borderRadius: 8, padding: '18px 20px', boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 10 }}>Requirements</div>
              {job.requirements.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: "var(--text-primary)", padding: '5px 0', lineHeight: 1.5 }}>
                  <span style={{ color: "var(--text-muted)", flexShrink: 0 }}>•</span>
                  <span>{r}</span>
                </div>
              ))}
            </div>
          )}

          {/* Sample Task */}
          {job.details?.sampleTask && (
            <div style={{ background: "var(--bg-card)", borderRadius: 8, padding: '18px 20px', boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 10 }}>Sample Task</div>
              <div style={{ padding: '12px 14px', background: "var(--bg-stat)", borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, fontFamily: 'var(--font-mono)', color: "var(--text-primary)", lineHeight: 1.6 }}>{job.details.sampleTask}</div>
            </div>
          )}
        </div>

        {/* Right: Sidebar — details + apply */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignSelf: 'start', position: 'sticky', top: 20 }}>
          {/* Apply card */}
          <div style={{ background: "var(--bg-card)", borderRadius: 8, padding: '18px 20px', boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.48px', marginBottom: 12 }}>Apply</div>
            {isApplied ? (
              <div style={{ padding: '10px', textAlign: 'center', fontSize: 12, fontWeight: 500, color: "var(--text-muted)", background: "var(--bg-stat)", borderRadius: 6 }}>
                {isStarted ? 'Working on this job' : acceptedJobs.has(jobId) ? 'Accepted — Onboarding' : 'Applied · Under Review'}
              </div>
            ) : applyState === 'confirming' ? (
              <button onClick={() => { applyToJob(jobId); setApplyState('applied'); }} style={{ width: '100%', padding: '10px', fontSize: 12, fontWeight: 500, background: "var(--bg)", color: "var(--text-primary)", border: 'none', borderRadius: 6, cursor: 'pointer' }}>Confirm Application</button>
            ) : (
              <button onClick={() => setApplyState('confirming')} style={{ width: '100%', padding: '10px', fontSize: 12, fontWeight: 500, background: "var(--bg)", color: "var(--text-primary)", border: 'none', borderRadius: 6, cursor: 'pointer' }}>Apply Now</button>
            )}
          </div>

          {/* Job details card */}
          <div style={{ background: "var(--bg-card)", borderRadius: 8, padding: '18px 20px', boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.48px', marginBottom: 12 }}>Details</div>
            {[
              { label: 'Domain', value: job.domain },
              { label: 'Rate', value: rateRange },
              { label: 'Team Size', value: job.teamSize ? `${job.teamSize} positions` : '—' },
              { label: 'Hours/Week', value: job.hoursPerWeek || '—' },
              { label: 'Location', value: job.location || 'Remote' },
              { label: 'Start Date', value: job.startDate || '—' },
              { label: 'Duration', value: job.duration || '—' },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
                <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{item.label}</span>
                <span style={{ fontSize: 11, fontWeight: 500, color: "var(--text-primary)" }}>{item.value}</span>
              </div>
            ))}
          </div>

          {/* Tools + Schedule */}
          {(job.details?.tools || job.details?.schedule || job.details?.qualityExpectations) && (
            <div style={{ background: "var(--bg-card)", borderRadius: 8, padding: '18px 20px', boxShadow: 'rgba(0,0,0,0.04) 0px 1px 3px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-primary)", textTransform: 'uppercase', letterSpacing: '0.48px', marginBottom: 12 }}>Work Environment</div>
              {job.details?.tools && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 3 }}>Tools</div>
                  <div style={{ fontSize: 12, color: "var(--text-primary)" }}>{job.details.tools}</div>
                </div>
              )}
              {job.details?.schedule && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 3 }}>Schedule</div>
                  <div style={{ fontSize: 12, color: "var(--text-primary)" }}>{job.details.schedule}</div>
                </div>
              )}
              {job.details?.qualityExpectations && (
                <div>
                  <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 3 }}>Quality Standards</div>
                  <div style={{ fontSize: 12, color: "var(--text-primary)", lineHeight: 1.5 }}>{job.details.qualityExpectations}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
